/**
 * Refund-Lage eines Mollie-Attempts — gemeinsame Quelle für die ActionBar
 * (welche Erstattungs-Aktionen noch angeboten werden) und den Status-Tag der
 * Timeline-Card.
 *
 * Vorher lag diese Logik nur in `ActionBar.tsx`, während die Timeline-Card mit
 * einem eigenen `hasPartialRefundLogic` arbeitete, das weder den Refund-Status
 * filterte noch einen "voll erstattet"-Zweig kannte. Beide konnten sich damit
 * über dieselbe Zahlung widersprechen: Die ActionBar bot bei voller Erstattung
 * korrekt keine Refund-Aktion mehr an, der Tag daneben las weiter "Bezahlt".
 *
 * Node 14 / TypeScript 4.7 kompatibel (kein `.at`, kein `Object.hasOwn`).
 */

import {MollieRefundState} from "./mollieStatusTagColor";

// ─── Refund-Cap-Bugfix (Prod-Incident B-104) ─────────────────────────────────
// Komplettiert Commit 03cf08b65 (Refund-Cap), der die ActionBar NICHT angefasst
// hatte. Vorher: `computeHasPartialRefund` kannte keinen "voll erstattet"-Zweig
// → bei voll (inkl. pending) erstatteten Zahlungen wurden weiterhin
// "Vollständig/Teilweise erstatten" angeboten; der Klick scheiterte am
// Backend-Guard 3c ("max. 0.00 € erstattbar"). Jetzt: Status-gefiltertes
// totalRefunded + "voll erstattet"-Erkennung + Cap auf Restbetrag.

/** Epsilon analog Backend-Guard 3c (mollie.payment.ts). */
export const REFUND_EPSILON = 0.001;

// Refund-Status, die den Betrag bei Mollie WIEDER FREIGEBEN und daher NICHT
// als "erstattet" mitzählen dürfen. Vergleich case-insensitiv: das interne
// Schema-Enum (MollieRefundStatus) serialisiert PascalCase ("Failed"), die
// rohen Mollie-Wire-Werte wären lowercase ("failed"/"canceled" — 1 l). Beide
// Schreibweisen werden hier abgedeckt.
const REFUND_FREEING_STATUSES = ["failed", "canceled"];

function isRefundCounting(status?: string | null): boolean {
    if (!status) return true;
    return REFUND_FREEING_STATUSES.indexOf(status.toLowerCase()) === -1;
}

/** Nur die Felder, die die Refund-Lage bestimmen — erfüllt von Timeline- und ActionBar-Shape. */
export interface RefundSummaryAttemptShape {
    status: string;
    amount?: number | null;
    refunds?: ReadonlyArray<{amount?: number | null; status?: string | null}> | null;
}

export interface RefundSummary {
    amount: number;
    totalRefunded: number;
    remaining: number;
    isFullyRefunded: boolean;
    hasPartialRefund: boolean;
}

/**
 * computeRefundSummary — fasst die Refund-Lage eines Attempts zusammen.
 *
 * Zählt nur Refunds, deren Status den Betrag NICHT wieder freigibt
 * (failed/canceled zählen NICHT — siehe REFUND_FREEING_STATUSES).
 *
 * - isFullyRefunded:  amount > 0 && totalRefunded >= amount - ε
 * - hasPartialRefund: totalRefunded > 0 && totalRefunded < amount - ε
 * - remaining:        amount - totalRefunded (auf >= 0 geklemmt)
 *
 * Für status !== "paid" werden refund-bezogene Flags neutral (false) gehalten;
 * `remaining`/`amount` bleiben dennoch korrekt befüllt.
 */
export function computeRefundSummary(attempt: RefundSummaryAttemptShape): RefundSummary {
    const amount = Number(attempt.amount || 0);
    const refunds = attempt.refunds || [];
    const totalRefunded = refunds.reduce((sum, r) => (isRefundCounting(r.status) ? sum + Number(r.amount || 0) : sum), 0);
    const remaining = Math.max(0, amount - totalRefunded);

    if (attempt.status !== "paid") {
        return {amount, totalRefunded, remaining, isFullyRefunded: false, hasPartialRefund: false};
    }

    const isFullyRefunded = amount > 0 && totalRefunded >= amount - REFUND_EPSILON;
    const hasPartialRefund = !isFullyRefunded && totalRefunded > 0 && totalRefunded < amount - REFUND_EPSILON;

    return {amount, totalRefunded, remaining, isFullyRefunded, hasPartialRefund};
}

/** Refund-Lage → Status-Tag-Variante (`getMollieStatusTagDescriptor`). */
export function refundStateFromSummary(summary: RefundSummary): MollieRefundState {
    if (summary.isFullyRefunded) {
        return "full";
    }

    return summary.hasPartialRefund ? "partial" : "none";
}
